import React from 'react';
import { motion } from 'framer-motion';
import { PartnerPortalLayout } from './PartnerPortalLayout';
import { DollarSignIcon, PercentIcon, TrendingUpIcon, TargetIcon } from 'lucide-react';
export function PerformancePage() {
  const kpis = [{
    label: 'Incremental Revenue',
    value: '$850,000',
    trend: '↗ 41% Lift',
    trendUp: true,
    icon: DollarSignIcon,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600'
  }, {
    label: 'Attach Rate',
    value: '18.5%',
    subtext: 'Of eligible group bookings',
    trend: '↗ +2.4%',
    trendUp: true,
    icon: PercentIcon,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600'
  }, {
    label: 'Avg. Upgrade Value',
    value: '$32,500',
    subtext: 'Per signed retreat',
    icon: TrendingUpIcon,
    iconBg: 'bg-violet-50',
    iconColor: 'text-violet-600'
  }, {
    label: 'Upsell Conversion',
    value: '22%',
    subtext: 'Proposal to Closed-Won',
    icon: TargetIcon,
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600'
  }];
  const skus = [{
    name: 'AI Leadership Retreat',
    revenue: '$520,000',
    bookings: 16,
    avg: '$32.5k',
    percent: 61
  }, {
    name: 'Executive Alignment Retreat',
    revenue: '$210,000',
    bookings: 8,
    avg: '$26.2k',
    percent: 25
  }, {
    name: 'Custom Wellness Track',
    revenue: '$120,000',
    bookings: 12,
    avg: '$10k',
    percent: 14
  }];
  return <PartnerPortalLayout data-id="element-5142">
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-id="element-5143">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-8" data-id="element-5144">
          <h1 className="font-serif-display text-3xl md:text-4xl font-bold text-[#0B1B3B] mb-2" data-id="element-5145">
            Commercial Performance
          </h1>
          <p className="text-slate-500" data-id="element-5146">
            Track the financial impact of your retreat upsell program.
          </p>
        </motion.div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8" data-id="element-5147">
          {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          return <motion.div key={kpi.label} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex flex-col" data-id="element-5148">
                <div className="flex items-start justify-between mb-8" data-id="element-5149">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${kpi.iconBg} ${kpi.iconColor}`} data-id="element-5150">
                    <Icon className="w-5 h-5" data-id="element-5151" />
                  </div>
                  {kpi.trend && <div className={`text-xs font-bold px-2 py-1 rounded-md ${kpi.trendUp ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-id="element-5152">
                      {kpi.trend}
                    </div>}
                </div>
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-5153">
                  {kpi.label}
                </div>
                <div className="font-serif-display text-3xl font-bold text-[#0B1B3B] mb-1" data-id="element-5154">
                  {kpi.value}
                </div>
                {kpi.subtext && <div className="text-xs text-slate-400 mt-auto pt-2" data-id="element-5155">
                    {kpi.subtext}
                  </div>}
              </motion.div>;
        })}
        </div>

        <div className="grid lg:grid-cols-2 gap-6" data-id="element-5156">
          {/* Revenue by SKU */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-5157">
            <h2 className="font-serif-display text-xl font-bold text-[#0B1B3B] mb-6" data-id="element-5158">
              Revenue by Retreat SKU
            </h2>
            <div className="space-y-6" data-id="element-5159">
              {skus.map((sku, i) => <div key={i} data-id="element-5160">
                  <div className="flex justify-between items-end mb-2" data-id="element-5161">
                    <div className="font-bold text-[#0B1B3B]" data-id="element-5162">{sku.name}</div>
                    <div className="font-bold text-emerald-600" data-id="element-5163">
                      {sku.revenue}
                    </div>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden mb-2" data-id="element-5164">
                    <div className="h-full bg-[#0B1B3B] rounded-full" style={{
                  width: `${sku.percent}%`
                }} data-id="element-5165" />
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-5166">
                    {sku.bookings} Bookings • Avg {sku.avg}
                  </div>
                </div>)}
            </div>
          </motion.div>

          {/* Monthly Chart Placeholder */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex flex-col" data-id="element-5167">
            <h2 className="font-serif-display text-xl font-bold text-[#0B1B3B] mb-6" data-id="element-5168">
              Monthly Upsell Revenue
            </h2>
            <div className="flex-1 flex items-end gap-2 h-48 pt-4" data-id="element-5169">
              {[30, 45, 25, 60, 75, 40, 85, 55, 90, 65, 100, 80].map((height, i) => <div key={i} className="flex-1 flex flex-col justify-end group relative" data-id="element-5170">
                    <div className="w-full bg-slate-100 group-hover:bg-amber-200 transition-colors rounded-t-sm" style={{
                height: `${height}%`
              }} data-id="element-5171" />
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-[#0B1B3B] text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10" data-id="element-5172">
                      ${(height * 1200).toLocaleString()}
                    </div>
                  </div>)}
            </div>
            <div className="flex justify-between mt-4 text-xs font-bold text-slate-400 uppercase" data-id="element-5173">
              <span data-id="element-5174">Jan</span>
              <span data-id="element-5175">Jun</span>
              <span data-id="element-5176">Dec</span>
            </div>
          </motion.div>
        </div>
      </div>
    </PartnerPortalLayout>;
}